import { MoDataStore } from 'ui-common';
import { Composition, CompositionRequest, CompositionPublishMetadataRequest, CompositionPublishMetadata, CompositionBuilderRequest } from './interfaces';
import { CompositionStoreMockPlugin } from './mocks/CompositionStoreMockPlugin';

/**
 * CompositionStore manages composition metadata and current page state.
 *
 * Responsibilities:
 * - Fetch composition metadata from /api/v1/compositions
 * - Track current active composition
 * - Cache composition list
 * - Notify subscribers of composition changes
 */
export class CompositionStore extends MoDataStore {
  static readonly defaultConfig = {
    mockPlugin: CompositionStoreMockPlugin,

    api: {
      service: 'compositions',
      list: 'getCompositions',
      get: 'getComposition',
    },
  };

  /** Current active composition being viewed/edited */
  protected currentComposition: Composition | null = null;

  constructor(config: { mock?: boolean } = {}) {
    super(config);
  }

  /**
   * Initialize the store
   *
   * @param initLoad - If false, skips the initial data load (useful for tests)
   * @returns this for method chaining
   */
  initialize(initLoad?: boolean): this {
    super.initialize(initLoad);
    return this;
  }

  /**
   * Fetch all compositions for the current customer
   *
   * @returns Promise<Composition[]> - Array of composition objects
   * @throws Error if API request fails
   */
  async getCompositions(): Promise<Composition[]> {
    try {
      const compositions: Composition[] = await this.callApiMethod({
        service: 'compositions',
        name: 'getCompositions'
      }, {});

      // Store compositions in the data store
      this.setData(compositions, { action: 'load' });
      return compositions;
    } catch (error) {
      console.error('Failed to fetch compositions');
      throw error;
    }
  }

  /**
   * Fetch a single composition by ID
   *
   * @param compositionId - The composition ID
   * @returns Promise<Composition | null> - The composition object or null if not found
   * @throws Error if API request fails
   */
  async getComposition(compositionId: string): Promise<Composition | null> {
    try {
      const composition: Composition | null = await this.callApiMethod({
        service: 'compositions',
        name: 'getComposition',
        pathParams: { compositionId }
      }, {});

      return composition;
    } catch (error) {
      console.error('Failed to fetch composition');
      throw error;
    }
  }

  /**
   * Fetch a composition for the builder view
   *
   * @param compositionId - The composition ID
   * @returns Promise<Composition> - The composition object with builder-specific data
   * @throws Error if composition not found or API request fails
   */
  async getCompositionForBuilder(compositionId: string): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'getCompositionForBuilder',
        pathParams: { compositionId }
      }, {});

      return composition;
    } catch (error) {
      console.error('Failed to fetch composition for builder');
      throw error;
    }
  }

  /**
   * Create a new composition
   *
   * @param compositionData - The composition data to create
   * @returns Promise<Composition> - The newly created composition
   * @throws Error if API request fails
   */
  async createComposition(compositionData: CompositionRequest): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'createComposition'
      }, compositionData);

      // Add the new composition to the cached data
      if (this.data) {
        this.setData([...this.data, composition], { action: 'create', item: composition });
      }

      return composition;
    } catch (error) {
      console.error('Failed to create composition');
      throw error;
    }
  }

  /**
   * Update an existing composition
   *
   * @param compositionId - The composition ID to update
   * @param compositionData - The composition data to update
   * @returns Promise<Composition> - The updated composition
   * @throws Error if API request fails
   */
  async updateComposition(
    compositionId: string,
    compositionData: CompositionRequest
  ): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'updateComposition',
        pathParams: { compositionId }
      }, compositionData);

      // Update the composition in the cached data
      if (this.data) {
        const updatedData = this.data.map((comp: Composition) =>
          comp.id === compositionId ? composition : comp
        );
        this.setData(updatedData, { action: 'update', item: composition });
      }

      // Update current composition if it's the one being updated
      if (this.currentComposition?.id === compositionId) {
        this.setCurrentComposition(composition);
      }

      return composition;
    } catch (error) {
      console.error('Failed to update composition');
      throw error;
    }
  }

  /**
   * Update composition builder configuration
   *
   * @param compositionId - The composition ID to update
   * @param builderData - The composition builder configuration data
   * @returns Promise<Composition> - The updated composition
   * @throws Error if API request fails
   */
  async updateCompositionBuilder(
    compositionId: string,
    builderData: CompositionBuilderRequest
  ): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'updateCompositionBuilder',
        pathParams: { compositionId }
      }, builderData);

      // Update the composition in the cached data
      if (this.data) {
        const updatedData = this.data.map((comp: Composition) =>
          comp.id === compositionId ? composition : comp
        );
        this.setData(updatedData, { action: 'update', item: composition });
      }

      // Update current composition if it's the one being updated
      if (this.currentComposition?.id === compositionId) {
        this.setCurrentComposition(composition);
      }

      return composition;
    } catch (error) {
      console.error('Failed to update composition builder');
      throw error;
    }
  }

  /**
   * Delete a composition
   *
   * @param compositionId - The composition ID to delete
   * @returns Promise<void>
   * @throws Error if API request fails
   */
  async deleteComposition(compositionId: string): Promise<void> {
    try {
      await this.callApiMethod({
        service: 'compositions',
        name: 'deleteComposition',
        pathParams: { compositionId }
      }, {});

      // Remove the composition from the cached data
      if (this.data) {
        const deletedComposition = this.data.find((comp: Composition) => comp.id === compositionId);
        const updatedData = this.data.filter((comp: Composition) => comp.id !== compositionId);
        this.setData(updatedData, { action: 'delete', item: deletedComposition });
      }

      // Clear current composition if it's the one being deleted
      if (this.currentComposition?.id === compositionId) {
        this.clearCurrentComposition();
      }
    } catch (error) {
      console.error('Failed to delete composition');
      throw error;
    }
  }

  /**
   * Delete all versions of a composition by compositionId
   *
   * This deletes all versions associated with the given compositionId.
   *
   * @param compositionId - The composition ID (deletes all versions with this initialId)
   * @returns Promise<void>
   * @throws Error if API request fails
   */
  async deleteAllCompositionVersions(compositionId: string): Promise<void> {
    try {
      // Find the composition to get its initialId before deletion
      const composition = this.data?.find((comp: Composition) => comp.id === compositionId);
      const initialIdToDelete = composition?.initialId || compositionId;

      await this.callApiMethod({
        service: 'compositions',
        name: 'deleteAllCompositionVersions',
        pathParams: { compositionId }
      }, {});

      // Remove all versions from the cached data
      if (this.data) {
        // Remove all compositions with the same initialId
        const deletedCompositions = this.data.filter((comp: Composition) =>
          comp.initialId === initialIdToDelete
        );
        const updatedData = this.data.filter((comp: Composition) =>
          comp.initialId !== initialIdToDelete
        );

        this.setData(updatedData, {
          action: 'delete-all-versions',
          item: deletedCompositions
        });
      }

      // Clear current composition if it's one of the deleted versions
      // Use the already-captured initialIdToDelete since the composition has been removed from this.data
      if (this.currentComposition) {
        const currentInitialId = this.currentComposition.initialId || this.currentComposition.id;

        if (currentInitialId === initialIdToDelete) {
          this.clearCurrentComposition();
        }
      }
    } catch (error) {
      console.error('Failed to delete composition versions');
      throw error;
    }
  }

  /**
   * Restore a composition version
   *
   * Creates a new version based on the specified composition ID.
   * The restored composition will have a new ID and incremented version number.
   *
   * @param compositionId - The composition ID to restore from
   * @returns Promise<Composition> - The newly created composition from the restore
   * @throws Error if API request fails
   */
  async restoreComposition(compositionId: string): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'restoreComposition',
        pathParams: { compositionId }
      }, {});

      // Add the restored composition to the cached data
      if (this.data) {
        this.setData([...this.data, composition], { action: 'restore', item: composition });
      }

      return composition;
    } catch (error) {
      console.error('Failed to restore composition');
      throw error;
    }
  }

  /**
   * Publish a composition version
   *
   * Publishes a composition to make it publicly accessible at a specific URL.
   *
   * @param compositionId - The composition ID to publish
   * @param publishData - Publication metadata
   * @returns Promise<CompositionPublishMetadata> - Publication details including host URL
   * @throws Error if API request fails
   */
  async publishComposition(
    compositionId: string,
    publishData: CompositionPublishMetadataRequest
  ): Promise<CompositionPublishMetadata> {
    try {
      const publishResponse: CompositionPublishMetadata = await this.callApiMethod({
        service: 'compositions',
        name: 'publishComposition',
        pathParams: { compositionId }
      }, publishData);

      // Update the composition's published status in the cache
      if (this.data) {
        const updatedData = this.data.map((comp: Composition) =>
          comp.id === compositionId
            ? { ...comp, published: true }
            : comp
        );
        this.setData(updatedData, { action: 'publish', item: publishResponse });
      }

      // Update current composition if it's the one being published
      if (this.currentComposition?.id === compositionId) {
        this.setCurrentComposition({
          ...this.currentComposition,
          published: true
        });
      }

      return publishResponse;
    } catch (error) {
      console.error('Failed to publish composition');
      throw error;
    }
  }

  /**
   * Get the published version of a composition
   *
   * Fetches the currently published version for a composition group (by initialId).
   *
   * @param compositionId - The composition ID (uses initialId to find published version)
   * @returns Promise<Composition> - The published composition version
   * @throws Error if no published version exists or API request fails
   */
  async getPublishedComposition(compositionId: string): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'getPublishedComposition',
        pathParams: { compositionId }
      }, {});

      return composition;
    } catch (error) {
      console.error('Failed to fetch published composition');
      throw error;
    }
  }

  /**
   * Get the latest version of a composition
   *
   * Fetches the most recent version for a composition group (by initialId).
   *
   * @param compositionId - The composition ID (uses initialId to find latest version)
   * @returns Promise<Composition> - The latest composition version
   * @throws Error if API request fails
   */
  async getLatestComposition(compositionId: string): Promise<Composition> {
    try {
      const composition: Composition = await this.callApiMethod({
        service: 'compositions',
        name: 'getLatestComposition',
        pathParams: { compositionId }
      }, {});

      return composition;
    } catch (error) {
      console.error('Failed to fetch latest composition');
      throw error;
    }
  }

  /**
   * Get version history of a composition
   *
   * Fetches all versions for a composition group (by initialId), ordered by version number descending.
   *
   * @param compositionId - The composition ID (uses initialId to find all versions)
   * @returns Promise<Composition[]> - Array of composition versions, newest first
   * @throws Error if API request fails
   */
  async getCompositionHistory(compositionId: string): Promise<Composition[]> {
    try {
      const compositions: Composition[] = await this.callApiMethod({
        service: 'compositions',
        name: 'getCompositionHistory',
        pathParams: { compositionId }
      }, {});

      return compositions;
    } catch (error) {
      console.error('Failed to fetch composition history');
      throw error;
    }
  }

  /**
   * Set the current active composition
   * This is the composition currently being viewed/edited
   *
   * Notifies subscribers with action: 'current-composition-changed'
   *
   * @param composition - The composition to set as current (or null to clear)
   */
  setCurrentComposition(composition: Composition | null): void {
    this.currentComposition = composition;

    // Notify subscribers of the change
    this.notifyUpdates({
      action: 'current-composition-changed',
      item: composition
    });
  }

  /**
   * Get the current active composition
   *
   * @returns The current composition or null if none is set
   */
  getCurrentComposition(): Composition | null {
    return this.currentComposition;
  }

  /**
   * Get composition by name (for URL-based lookup)
   *
   * First checks the cached data, then fetches all compositions if needed.
   * This is useful when navigating to a page by composition name in the URL.
   *
   * @param name - The composition name from URL (e.g., "product-dashboard")
   * @returns Promise<Composition | null> - The matching composition or null
   */
  async getCompositionByName(name: string): Promise<Composition | null> {
    // First try to find in cached data
    if (this.data && this.data.length > 0) {
      const found = this.data.find((comp: Composition) => comp.name === name);
      if (found) return found;
    }

    // If not in cache, fetch all and search
    try {
      const compositions = await this.getCompositions();
      return compositions.find(comp => comp.name === name) || null;
    } catch (error) {
      console.error('Failed to fetch composition by name');
      throw error;
    }
  }

  /**
   * Check if compositions have been loaded
   *
   * @returns true if compositions have been fetched, false otherwise
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Check if compositions are currently loading
   *
   * @returns true if a fetch operation is in progress
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * Clear the current composition (e.g., on logout or navigation away)
   */
  clearCurrentComposition(): void {
    this.setCurrentComposition(null);
  }

  /**
   * Override clear to also clear current composition
   *
   * @param reload - If true, triggers a reload
   */
  clear(reload?: boolean): void {
    this.clearCurrentComposition();
    super.clear(reload);
  }
}
